/**
 * Email Template API Service
 * Handles all template operations: save, load, list, delete, favorite
 */

class TemplateApiError extends Error {
  constructor(message, status, payload) {
    super(message);
    this.name = 'TemplateApiError';
    this.status = status;
    this.payload = payload;
  }
}

const getTemplateApiBase = () => {
  const base =
    process.env.REACT_APP_API_BASE ||
    process.env.REACT_APP_API_BASE_URL ||
    '';
  
  if (typeof window !== 'undefined' && !base) {
    return `${window.location.origin}/api/email_templates.php`;
  }
  
  return base ? `${base.replace(/\/$/, '')}/api/email_templates.php` : '/api/email_templates.php';
};

async function templateRequest(action, { method = 'GET', data, token, queryParams } = {}) {
  const base = getTemplateApiBase();
  const params = new URLSearchParams({ action });
  
  if (queryParams) {
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        params.append(key, value);
      }
    });
  }

  const url = `${base}?${params.toString()}`;
  
  const headers = {
    'Content-Type': 'application/json',
    'X-Template-Action': action,
  };
  
  if (token) {
    headers.Authorization = `Bearer ${token}`;
    headers['X-Auth-Token'] = token;
  }

  const res = await fetch(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: 'include',
  });

  const contentType = res.headers.get('content-type') || '';
  const payload = contentType.includes('application/json') ? await res.json() : null;

  if (!res.ok) {
    const message =
      (payload && (payload.error || payload.message)) ||
      `Template API error (${res.status})`;
    throw new TemplateApiError(message, res.status, payload || {});
  }

  return payload;
}

export const templateApi = {
  /**
   * Save a new template or update existing
   */
  async saveTemplate(templateData, token) {
    return templateRequest('save', {
      method: 'POST',
      data: {
        id: templateData.id || '',
        name: templateData.name,
        subject: templateData.subject,
        body: templateData.body,
        category: templateData.category || 'general',
        description: templateData.description || '',
        tags: templateData.tags || [],
      },
      token,
    });
  },

  /**
   * Get a single template by ID
   */
  async getTemplate(templateId, token) {
    return templateRequest('get', {
      method: 'GET',
      queryParams: { id: templateId },
      token,
    });
  },

  /**
   * List all templates for current user
   */
  async listTemplates(filters = {}, token) {
    const queryParams = {};
    
    if (filters.category && filters.category !== 'all') {
      queryParams.category = filters.category;
    }
    
    if (filters.search) {
      queryParams.search = filters.search;
    }

    return templateRequest('list', {
      method: 'GET',
      queryParams,
      token,
    });
  },

  /**
   * Delete a template
   */
  async deleteTemplate(templateId, token) {
    return templateRequest('delete', {
      method: 'POST',
      data: { id: templateId },
      token,
    });
  },

  /**
   * Toggle favorite status
   */
  async toggleFavorite(templateId, isFavorite, token) {
    return templateRequest('favorite', {
      method: 'POST',
      data: { id: templateId, favorite: isFavorite },
      token,
    });
  },
};

export { TemplateApiError };
